import React from 'react';
import Slider from 'react-slick';
import classnames from 'classnames';

import Button from '@components/Button';
import ModalBase from '@components/ModalBase';

import { getEmptyErrorState, getFileType } from '@utils/AppUtil';

import AttachmentImage from './AttachmentImage';

import './style.scss';

export interface IAttachmentsDisplayModalProps {
  show: boolean;
  onClose: (e: React.SyntheticEvent<HTMLElement>) => void;
  attachments: IModalDisplayAttachment[];
  openOnIndex?: number;
  title: string;
  trackLabel?: string;
  className?: string;
  displayAttachmentDetails: boolean;
}

interface IAttachmentsDisplayModalState {
  currentSlide: number;
  modifiedAttachments: IModalDisplayAttachment[];
}

export default class AttachmentsDisplayModal extends React.Component<
  IAttachmentsDisplayModalProps,
  IAttachmentsDisplayModalState
> {
  constructor(props: IAttachmentsDisplayModalProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidMount() {
    this.setState({
      modifiedAttachments: this.getModifiedAttachmentsFromProps(this.props),
    });
  }

  componentWillReceiveProps(nextProps: IAttachmentsDisplayModalProps) {
    if (this.props.attachments !== nextProps.attachments) {
      this.setState({
        modifiedAttachments: this.getModifiedAttachmentsFromProps(nextProps),
      });
    }

    if (this.props.openOnIndex !== nextProps.openOnIndex) {
      this.setState({
        currentSlide: nextProps.openOnIndex || 0,
      });
    }
  }

  getEmptyState = (): IAttachmentsDisplayModalState => ({
    currentSlide: 0,
    modifiedAttachments: [],
  });

  getModifiedAttachmentsFromProps = (nextProps: IAttachmentsDisplayModalProps): IModalDisplayAttachment[] => {
    const { attachments } = nextProps;

    return attachments.map((attachment) => ({
      ...attachment,
      error: this.getFileEmptyErrorState(),
    }));
  };

  onClose = (e) => {
    this.props.onClose(e);
  };

  getThumb = (file: ICSAttachmentResponse) => {
    const defaultClassName = 'attachments-edit__saved-thumb';

    const fileTypeToThumb = {
      image: (
        <div className={classnames('flex-row--center', defaultClassName)}>
          <AttachmentImage src={file.fileURL} alt={file.fileName || 'Attachment preview'} />
        </div>
      ),
      others: <div className={`${defaultClassName} ${defaultClassName}--others`} />,
    };

    return fileTypeToThumb[getFileType(file)];
  };

  getFileEmptyErrorState = () => ({
    title: getEmptyErrorState(),
    description: getEmptyErrorState(),
  });

  readonlyModeFooter = () => <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onClose} content="Close" />;

  getFooter = () => this.readonlyModeFooter();

  renderAttachment = () => (attachment, index) =>
    (
      <div className={classnames('attachments-edit__carousel')} key={index}>
        {this.getThumb(attachment)}
      </div>
    );

  renderBody = () => {
    const sliderSettings = {
      dots: false,
      centerMode: true,
      infinite: false,
      vertical: false,
      speed: 500,
      slidesToShow: 1.1,
      slidesToScroll: 1,
      initialSlide: this.props.openOnIndex,
      afterChange: (currentSlide) => this.setState({ currentSlide: Math.ceil(currentSlide) }),
    };

    const viewingMessage = `Viewing ${this.state.currentSlide + 1} of ${this.props.attachments.length}`;

    return (
      <div className={classnames('attachments-edit__body')}>
        <label>{viewingMessage}</label>
        <Slider {...sliderSettings}>{this.state.modifiedAttachments.map(this.renderAttachment())}</Slider>
      </div>
    );
  };

  render() {
    const title = `${this.props.title} Details`;
    const className = this.props.className || '';

    return (
      <div>
        <ModalBase
          key={1}
          show={this.props.show}
          onClose={this.onClose}
          titleElement={title}
          bodyElement={this.renderBody()}
          footerElement={this.getFooter()}
          className={classnames('attachments-edit', className)}
        />
      </div>
    );
  }
}
